if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.observeTargetId = this.dataset.observeTarget;

        this.observer = null;
        this.loadingObserver = null;
        this.priceObserver = null;
        this.variantObserver = null;
        this.nativeSelect = null;
        this.stickySelect = null;
      }

      connectedCallback() {
        this.observeTarget = document.getElementById(
          this.observeTargetId
        );

        if (!this.observeTarget) return;

        this.realButton = document.getElementById(
          `ProductSubmitButton-${this.sectionId}`
        );

        this.setupObserver();
        this.syncVariantPicker();
        this.setupVariantSync();
        this.setupPriceObserver();
        this.setupProxyClick();
        this.setupLoadingSync();
        this.setupMobileToggle();

        this.syncAll();
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }

        if (this.loadingObserver) {
          this.loadingObserver.disconnect();
        }

        if (this.priceObserver) {
          this.priceObserver.disconnect();
        }

        if (this.variantObserver) {
          this.variantObserver.disconnect();
        }
      }

      setupObserver() {
        const buyButtons = this.observeTarget.querySelector(
          '.product-form, .product__info-container'
        );

        const target =
          this.realButton || buyButtons || this.observeTarget;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.classList.remove('is-visible');
              } else if (entry.boundingClientRect.top < 0) {
                this.classList.add('is-visible');
              } else {
                this.classList.remove('is-visible');
              }
            });
          },
          {
            threshold: 0,
            rootMargin: '0px 0px 0px 0px',
          }
        );

        this.observer.observe(target);
      }

      setupMobileToggle() {
        const toggleButton = this.querySelector(
          '.sticky-atc__toggle'
        );

        if (!toggleButton) return;

        toggleButton.addEventListener('click', () => {
          this.classList.toggle('is-collapsed');

          const isCollapsed =
            this.classList.contains('is-collapsed');

          toggleButton.setAttribute(
            'aria-expanded',
            String(!isCollapsed)
          );
        });
      }

      syncVariantPicker() {
        const stickyPickerWrapper = document.getElementById(
          `StickyVariantPicker-${this.sectionId}`
        );

        if (!stickyPickerWrapper) return;

        const nativeSelect = this.observeTarget.querySelector(
          'variant-selects select'
        );

        if (!nativeSelect) return;

        this.nativeSelect = nativeSelect;

        const clone = nativeSelect.cloneNode(true);

        clone.id = `StickySelect-${this.sectionId}`;

        stickyPickerWrapper.innerHTML = '';
        stickyPickerWrapper.appendChild(clone);

        this.stickySelect = clone;

        clone.addEventListener('change', () => {
          if (!this.nativeSelect) return;

          this.nativeSelect.value = clone.value;

          this.nativeSelect.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );

          /*
           * Dawn updates the product asynchronously.
           * The MutationObserver will catch the final state,
           * while this provides an immediate synchronization.
           */
          setTimeout(() => {
            this.syncAll();
          }, 150);
        });

        this.syncStickyVariant();
      }

      setupVariantSync() {
        this.nativeSelect =
          this.nativeSelect ||
          this.observeTarget.querySelector(
            'variant-selects select'
          );

        if (!this.nativeSelect) return;

        this.nativeSelect.addEventListener('change', () => {
          this.syncStickyVariant();

          setTimeout(() => {
            this.syncAll();
          }, 150);
        });

        /*
         * Watch the product info area because Dawn replaces
         * variant-dependent markup after a variant change.
         */
        this.variantObserver = new MutationObserver(() => {
          this.syncStickyVariant();
          this.syncButtonState();
          this.mirrorPrice();
        });

        this.variantObserver.observe(this.observeTarget, {
          childList: true,
          subtree: true,
        });
      }

      setupPriceObserver() {
        const priceContainer = this.observeTarget.querySelector(
          `#price-${this.sectionId}`
        );

        if (!priceContainer) return;

        this.priceObserver = new MutationObserver(() => {
          this.mirrorPrice();
          this.syncButtonState();
        });

        this.priceObserver.observe(priceContainer, {
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      syncStickyVariant() {
        if (!this.stickySelect) return;

        const nativeSelect =
          this.nativeSelect ||
          this.observeTarget.querySelector(
            'variant-selects select'
          );

        if (!nativeSelect) return;

        if (
          nativeSelect.value &&
          this.stickySelect.value !== nativeSelect.value
        ) {
          this.stickySelect.value = nativeSelect.value;
        }
      }

      syncAll() {
        this.syncStickyVariant();
        this.mirrorPrice();
        this.syncButtonState();
      }

      /*
       * Copies ONLY the actual price content.
       *
       * We intentionally do NOT copy the complete source .price
       * innerHTML because the main product price may contain
       * Sale / Sold Out badges. The sticky ATC was rendered with
       * show_badges: false, so those badges must never appear here.
       */
      mirrorPrice() {
        if (!this.observeTarget) return;

        const sourcePrice = this.observeTarget.querySelector(
          `#price-${this.sectionId} .price`
        );

        const stickyPrice = document.getElementById(
          `StickyPrice-${this.sectionId}`
        );

        if (!sourcePrice || !stickyPrice) return;

        const sourceContainer = sourcePrice.querySelector(
          '.price__container'
        );

        if (!sourceContainer) return;

        /*
         * The sticky price already has its own .price wrapper.
         * Only replace the inner price container.
         */
        const nextHTML = sourceContainer.outerHTML;

        const currentContainer = stickyPrice.querySelector(
          '.price__container'
        );

        if (!currentContainer) {
          stickyPrice.innerHTML = nextHTML;
        } else if (
          currentContainer.outerHTML !== nextHTML
        ) {
          currentContainer.outerHTML = nextHTML;
        }

        /*
         * Keep only the useful price state classes.
         * Never copy badge markup.
         */
        const sourceClasses = sourcePrice.classList;

        const stickyPriceElement =
          stickyPrice.querySelector('.price');

        if (!stickyPriceElement) return;

        stickyPriceElement.classList.toggle(
          'price--on-sale',
          sourceClasses.contains('price--on-sale')
        );

        stickyPriceElement.classList.toggle(
          'price--no-compare',
          sourceClasses.contains('price--no-compare')
        );

        stickyPriceElement.classList.toggle(
          'price--sold-out',
          sourceClasses.contains('price--sold-out')
        );

        /*
         * Explicitly remove any badge elements that may have
         * been introduced by a previous version of the script.
         */
        stickyPriceElement
          .querySelectorAll(
            '.price__badge-sale, .price__badge-sold-out, .badge'
          )
          .forEach((badge) => badge.remove());
      }

      /*
       * Keeps the sticky Add to Cart buttons synchronized with
       * the actual product form button.
       *
       * This is important when the selected variant changes
       * between available and sold-out states.
       */
      syncButtonState() {
        const stickyButtons = [
          document.getElementById(
            `StickyAddToCart-${this.sectionId}`
          ),
          document.getElementById(
            `StickyAddToCartMobile-${this.sectionId}`
          ),
        ].filter(Boolean);

        if (!stickyButtons.length) return;

        if (!this.realButton) return;

        const isDisabled =
          this.realButton.disabled ||
          this.realButton.getAttribute('aria-disabled') ===
            'true';

        const realText = this.realButton.querySelector(
          'span:not(.loading__spinner)'
        );

        const realButtonText =
          realText?.textContent?.trim();

        stickyButtons.forEach((button) => {
          /*
           * Don't interfere with loading state.
           */
          if (
            button.classList.contains('is-loading')
          ) {
            return;
          }

          button.disabled = isDisabled;

          const textElement = button.querySelector(
            'span:not(.loading__spinner)'
          );

          if (
            textElement &&
            realButtonText
          ) {
            textElement.textContent = realButtonText;
          }
        });
      }

      /*
       * Sticky buttons proxy the real product form button.
       * This preserves HulkApps validation and Dawn's normal
       * add-to-cart behavior.
       */
      setupProxyClick() {
        if (!this.realButton) return;

        const stickyButtons = [
          document.getElementById(
            `StickyAddToCart-${this.sectionId}`
          ),
          document.getElementById(
            `StickyAddToCartMobile-${this.sectionId}`
          ),
        ].filter(Boolean);

        stickyButtons.forEach((button) => {
          button.addEventListener('click', () => {
            if (button.disabled) return;

            this.realButton.click();

            setTimeout(() => {
              const error =
                this.observeTarget.querySelector(
                  '.validation_error, .product-form__error-message-wrapper:not([hidden])'
                );

              if (error) {
                error.scrollIntoView({
                  behavior: 'smooth',
                  block: 'center',
                });
              }
            }, 120);
          });
        });
      }

      setupLoadingSync() {
        if (!this.realButton) return;

        const stickyButtons = [
          document.getElementById(
            `StickyAddToCart-${this.sectionId}`
          ),
          document.getElementById(
            `StickyAddToCartMobile-${this.sectionId}`
          ),
        ].filter(Boolean);

        const applyLoadingState = () => {
          const realSpinner =
            this.realButton.querySelector(
              '.loading__spinner'
            );

          const spinnerVisible =
            realSpinner &&
            !realSpinner.classList.contains('hidden');

          stickyButtons.forEach((button) => {
            button.classList.toggle(
              'is-loading',
              !!spinnerVisible
            );

            button.disabled = !!spinnerVisible;

            if (!spinnerVisible) {
              this.syncButtonState();
            }
          });
        };

        this.loadingObserver = new MutationObserver(
          applyLoadingState
        );

        this.loadingObserver.observe(
          this.realButton,
          {
            attributes: true,
            attributeFilter: [
              'disabled',
              'aria-disabled',
              'class',
            ],
            subtree: true,
            childList: true,
          }
        );

        applyLoadingState();
      }
    }
  );
}